import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-search-eye-line',
    title: 'Análise do Caso',
    desc: 'Você nos conta sua situação e analisamos gratuitamente se há fundamento jurídico para ação. Sem compromisso, sem custo inicial.',
  },
  {
    number: '02',
    icon: 'ri-lightbulb-line',
    title: 'Orientação Estratégica',
    desc: 'Apresentamos a melhor estratégia jurídica para o seu caso, explicando cada etapa de forma clara e acessível.',
  },
  {
    number: '03',
    icon: 'ri-file-list-3-line',
    title: 'Envio de Documentos',
    desc: 'Orientamos quais documentos são necessários e como obtê-los. Todo o processo pode ser feito de forma online e segura.',
  },
  {
    number: '04',
    icon: 'ri-shield-check-line',
    title: 'Acompanhamento Completo',
    desc: 'Cuidamos de todo o processo judicial, mantendo você informado em cada etapa até a resolução final do seu caso.',
  },
];

function StepItem({ step, index, visible }: { step: typeof steps[0]; index: number; visible: boolean }) {
  return (
    <div
      className="flex flex-col md:flex-row items-start gap-6 md:gap-8"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 150}ms, transform 0.7s ease ${index * 150}ms`,
      }}
    >
      {/* Number + Line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-brand-gold text-brand-black font-serif font-bold text-xl md:text-2xl">
          {step.number}
        </div>
        {index < steps.length - 1 && (
          <div className="w-0.5 h-12 md:h-16 bg-gradient-to-b from-brand-gold to-brand-gold/10 mt-2" />
        )}
      </div>

      {/* Content */}
      <div className="pb-8 md:pb-12 pt-2">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`${step.icon} text-brand-gold text-xl`} />
          </div>
          <h3 className="font-sans text-brand-black text-xl md:text-2xl font-bold">{step.title}</h3>
        </div>
        <p className="font-sans text-brand-green/70 text-base leading-relaxed max-w-lg">{step.desc}</p>
      </div>
    </div>
  );
}

export default function HowItWorksSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="como-funciona" className="bg-white py-20 md:py-28 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div
          className="text-center mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="text-brand-gold font-sans text-xs font-semibold tracking-widest uppercase">Processo</span>
          <h2 className="font-serif text-brand-black text-3xl md:text-5xl font-bold mt-3 leading-tight">
            Como Trabalhamos
          </h2>
          <p className="font-sans text-brand-green/70 text-base md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
            Um processo simples, transparente e totalmente online. Do primeiro contato até a resolução do seu caso.
          </p>
        </div>

        <div ref={ref} className="max-w-3xl mx-auto">
          {steps.map((step, i) => (
            <StepItem key={i} step={step} index={i} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}
